import { SelectBox, SelectMarket, SkuRotationPeriodType } from '@/interfaces/global-data';
import {
  CategoryStrategies,
  CompetitorsPriceMethods,
  CompetitorsPriceStrategies,
  IModuleItemSelectedProduct, InternalCodeAndProductRange,
  PercentileTypes,
  TaskInfoFilterType,
} from '@/interfaces/server-data';

export interface ILabelAndValue<T> {
  label: string;
  value: T;
}

export type TModuleType = 'sales' | 'promo';

export enum ModuleType {
  SALES = 'sales',
  PROMO = 'promo',
}

export interface IModuleItemSaveDto {
  id: number | null;
  startDate: string | null;
  endDate: string | null;
  name: string | null;
  type: string | null;
  stagesNumber: number | null;
  minSaleDiscount: number | null;
  maxSaleDiscount: number | null;
  minPromotionDiscount: number | null;
  maxPromotionDiscount: number | null;
  minMargin: number | null;
  revaluationFileName: string | null;
}

export interface IModuleItem extends IModuleItemSaveDto {
  id: number | null;
  status: string | null;
  tradingId: number | null;
  groupIds: number[];
  categoryIds: number[];
  subcategoryId: number | null;
  clusterIds: number[];
  formatId: number | null;
  regionIds: number[];
}

export interface IidAndName {
  id: number;
  name: string;
}

export interface IModuleItemResponseDto {
  id: number | null;
  startDate: string | null;
  endDate: string | null;
  name: string | null;
  type: string | null;
  status: string | null;
  groups: IidAndName[];
  categories: IidAndName[];
  subcategory: IidAndName;
  clusters: IidAndName[];
  format: IidAndName;
  regions: IidAndName[];
  stagesNumber: number | null;
  minSaleDiscount: number | null;
  maxSaleDiscount: number | null;
  minPromotionDiscount: number | null;
  maxPromotionDiscount: number | null;
  minMargin: number | null;
  skuCount: number | null;
  remains: number | null;
  taskId: number | null;
  tradingId: number | null;
  revaluationFileName: string | null;
}

export interface IModuleItemOptionForSelectBox {
  value: number | null;
  label: string;
  groups: IidAndName[];
  categories: IidAndName[];
  subcategory: IidAndName;
  clusters: IidAndName[];
  format: IidAndName;
  regions: IidAndName[];
  minSaleDiscount?: number | null;
  maxSaleDiscount?: number | null;
  minPromotionDiscount?: number | null;
  maxPromotionDiscount?: number | null;
  status: string | null;
}

export interface IModuleItemTableItem {
  id: number | null;
  startDate: string | null;
  endDate: string | null;
  name: string | null;
  tradingId: number | null;
  status: string | null;
  type: string | null;
  groups: IidAndName[];
  categories: IidAndName[];
  subcategory: IidAndName;
  clusters: IidAndName[];
  format: IidAndName;
  regions: IidAndName[];
  stagesNumber: number | null;
  minSaleDiscount: number | null;
  maxSaleDiscount: number | null;
  minPromotionDiscount: number | null;
  maxPromotionDiscount: number | null;
  minMargin: number | null;
  skuCount: number | null;
  remains: number | null;
  taskId: number | null;
  considerPlannedSeason?: boolean;
  revaluationFileName: string | null;
}

export interface IModuleItemFilterOptions {
  groups: IidAndName[];
  categories: IidAndName[];
  subcategories: IidAndName[];
  storeFormats: IidAndName[];
  storeRegions: IidAndName[];
  storeClusters: IidAndName[];
}

export interface IModuleItemQueryDto {
  tradingId: number | null;
  sortField?: string,
  isAsc?: boolean,
  page?: number,
  size?: number,
}

export interface IModuleItemFilters {
  market: SelectMarket | null;
  groupIds: number[];
  categoryIds: number[];
  subcategory: number | number[];
  clusterIds: number[];
  formatSelected: number;
  regionIds: number[];
}

export interface IModuleItemFiltersInLevels {
  market: SelectMarket | null;
  groupIds: number[];
  categoryIds: number[];
  subcategory: number;
  clusterIds: number[];
  formatSelected: number;
  regionIds: number[];
}

export type IModuleItemsStaticTableFilterOptions = {
  [key in TModuleType]: {
    statuses: ILabelAndValue<string>[];
    types: ILabelAndValue<string>[];
  };
}

export interface IModuleItemsBasicFilters {
  tradingId: number;
  groupsOfProduct: number[];
  categories: number[];
  subcategories: number[];
  storeFormats: number[];
  storeRegionValues: number[];
  storeClusters: number[];
}

export interface IModuleItemsProduct {
  internalCode: string | null;
  group: string | null;
  category: string | null;
  storeRegion: string | null;
  storeCluster: string | null;
  subcategory: string | null;
  minPrice: number | null;
  maxPrice: number | null;
  remains: number | null;
  minMargin: number | null;
  purchasePrice?: number | null;
  minSaleDiscount?: number | null;
  maxSaleDiscount?: number | null;
  minPromotionDiscount?: number | null;
  maxPromotionDiscount?: number | null;
}

export interface IModuleItemDeleteProductsFilter {
  tradingId: number | null;
  internalCodesAndSkuNames: IModuleItemSelectedProduct[];
  saleIdFilter?: string;
  promotionIdFilter?: string;
}

export type EntityFilterKeys =
    | TaskInfoFilterType.group
    | TaskInfoFilterType.category
    | TaskInfoFilterType.subcategory
    | TaskInfoFilterType.format
    | TaskInfoFilterType.region
    | TaskInfoFilterType.cluster;

export interface EntityMaskOptions {
  groups: ILabelAndValue<number>[],
  categories: ILabelAndValue<number>[],
  subcategories: ILabelAndValue<number>[],
  formats: ILabelAndValue<number>[],
  regions: ILabelAndValue<number>[],
  clusters: ILabelAndValue<number>[],
}

export interface TaskTypeEntityProperties {
  group: string;
  category: string;
  subcategory: string;
  format: string;
  region: string;
  cluster: string;
}

export interface EntityFilters {
  groupIds: number[],
  categoryIds: number[],
  subcategoryId: number | null,
  formatId: number | null,
  regionIds: number[],
  clusterIds: number[],
}

export interface AggregatedModuleReportModalFilters {
  groupIds: number[],
  categoryIds: number[],
  subcategoryIds: number[],
  formatIds: number[],
  regionIds: number[],
  clusterIds: number[],
  userIds: number[],
  dates: string[],
  types: string[],
  statuses: string[],
  reportStatuses: string[],
}

export interface BaseAggregatedReportPayloadParams {
  tradingId: number,
  taskIds?: number[],
  groups: number[],
  categories: number[],
  subcategories: number[],
  storeFormats: number[],
  storeRegions: number[],
  storeClusters: number[],
  userIds: number[],
  startDate: string,
  endDate: string,
}

export interface CreateAggregatedModuleReportPayloadParams extends BaseAggregatedReportPayloadParams {
  saleTypes: string[],
  saleStatuses: string[],
  resultStatus?: string[],
}

export interface CreateAggregatedPromotionsReportPayloadParams extends BaseAggregatedReportPayloadParams {
  promotionTypes: string[],
  promotionStatuses: string[],
  resultStatus?: string[],
}

export interface CategoryFiltersValues {
  groupId: number | null,
  categoryId: number | null,
  subcategoryId: number | null,
  formatId: number | null,
  regionId: number | null,
  clusterId: number | null,
}

export interface CategoryParamsValues {
  useProductRange: boolean;
  usePromoQuote: boolean;
  useSkuRotation: boolean;
  promoQuote: number | null;
  rotationPeriodType: SkuRotationPeriodType;
  useCompetitorsPromoPrices: boolean;
  calculateCompAllowedDeviationPromo: boolean;
  competitorPriceControlParameterPcPromo: number;
  competitorsPricePeriod: number;
  competitorsPromoPriceInfluencesCoef: number;
  percentileTypePromo: PercentileTypes;
  competitorsPromoPriceAdjustment: string;
  competitorsControlPromoParam: number;
  competitorsPromoPriceMethod: SelectBox<CompetitorsPriceMethods>;
  competitorsMinimumProfitPromo: number;
  competitorsMaximumProfitPromo: number;
  competitorsPromoPriceStrategy: SelectBox<CompetitorsPriceStrategies>;
  daysToPriceChangeStart?: number | null;
  daysToPriceChangeFinish?: number | null;
}

export interface CategoryFindFiltersPayload {
  tradingId: number | null;
  groupOfProduct: number | null;
  category: number | null;
  subcategories: number[] | null;
  productSegments: number[] | null;
  storeFormat: number | null;
  storeRegionValues: number[] | null;
  groupsOfProduct: number[] | null;
  categories: number[] | null;
  storeCluster: number | null | number[];
  seasonValues: number[] | null;
  categoryStrategy: CategoryStrategies;
  productRangesAndInternalCodes: InternalCodeAndProductRange[];
  downloadParamsWithSubcategory: boolean;
}

enum PromotionStatus {
  ACTIVE = 'ACTIVE',
  STOPPED = 'STOPPED',
  PLANNED = 'PLANNED'
}

export enum PromotionType {
  PRICE = 'PRICE',
  DISCOUNT = 'DISCOUNT',
}

export interface PromotionExportFilter {
  tradingNetId: number;
  activeDate?: string;
  id?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  statuses?: PromotionStatus[];
  types?: PromotionType[];
  name?: string | null;
  minPromotionDiscount?: number | null;
  maxPromotionDiscount?: number | null;
  minMargin?: number | null;
  remains?: number | null;
  groupIds?: number[];
  categoryIds?: number[];
  subcategoryIds?: number[];
  formatIds?: number[];
  regionIds?: number[];
  clusterIds?: number[];
  promotionIds?: number[];
}

export interface SelectCheckboxesData {
  isCommonCheckboxSelected: boolean;
  includedIds: number[];
  excludedIds: number[];
}

export interface PromotionsExportPayload {
  filter: PromotionExportFilter;
  selectCheckboxesData: SelectCheckboxesData;
}
